import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../apiClient';
import { useApp } from '../appContext';

const money = (n) => (n == null ? '—' : `$${Number(n).toFixed(2)}`);
const day = (d) => (d ? String(d).slice(0, 10) : '');

// Click-to-sort header, duplicated per the "no shared component layer" convention.
function SortTh({ label, sortKey, sort, onSort, style }) {
  const active = sort.key === sortKey;
  return (
    <th style={{ textAlign: 'left', cursor: 'pointer', userSelect: 'none', ...style }} onClick={() => onSort(sortKey)}>
      {label} <span style={{ opacity: active ? 1 : 0.25 }}>{active && sort.dir === 'desc' ? '▼' : '▲'}</span>
    </th>
  );
}

/**
 * Review and maintain every project's billing rates over time. One effective-dated series per kind:
 * Services ($/hr labor) and License (SW License run rate). Insert-only -- a change is a new row;
 * an admin may delete a row entered in error. See the BillingRate comment in schema.prisma.
 */
function HistoryPanel({ project, isAdmin, onClose, onChanged }) {
  const [rows, setRows] = useState([]);
  const [kind, setKind] = useState('Services');
  const [rate, setRate] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  const load = useCallback(async () => {
    try { setRows(await apiFetch(`/projects/${project.id}/billing-rates`)); }
    catch (err) { setError(err.message); }
  }, [project.id]);
  useEffect(() => { load(); }, [load]);

  async function add() {
    const n = Number(rate);
    if (rate === '' || !(n > 0)) { setError('Enter a rate greater than 0'); return; }
    if (!effectiveDate) { setError('Pick an effective date'); return; }
    setSaving(true); setError('');
    try {
      await apiFetch(`/projects/${project.id}/billing-rate`, { method: 'POST', body: JSON.stringify({ rate: n, kind, effectiveDate }) });
      setRate('');
      await load(); onChanged();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  }

  async function remove(id) {
    if (confirmId !== id) { setConfirmId(id); return; }
    setConfirmId(null);
    try { await apiFetch(`/billing-rates/${id}`, { method: 'DELETE' }); await load(); onChanged(); }
    catch (err) { setError(err.message); }
  }

  const today = new Date().toISOString().slice(0, 10);
  // Per kind, the first row on/before today (rows arrive newest-first) is the one in effect.
  const currentIds = new Set(['Services', 'License', 'Ratable', 'Provisional'].map(k => rows.find(r => r.kind === k && day(r.effectiveDate) <= today)?.id).filter(Boolean));

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <div className="card-title" style={{ margin: 0 }}>{project.name} — rate history</div>
        <button className="btn btn-outline" style={{ padding: '4px 10px', fontSize: 12 }} onClick={onClose}>Close</button>
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '12px 0' }}>
        <select className="form-select" style={{ width: 110 }} value={kind} onChange={(e) => setKind(e.target.value)}>
          <option>Services</option>
          <option>License</option>
          <option>Ratable</option>
          <option value="Provisional">Provisional (temp)</option>
        </select>
        <input type="number" min="0" step="0.01" className="form-input" style={{ width: 100 }} placeholder={kind === 'Ratable' ? '$/month' : '$/hr'}
          value={rate} onChange={(e) => setRate(e.target.value)} />
        <span style={{ fontSize: 12, color: 'var(--text-mid)' }}>effective</span>
        <input type="date" className="form-input" style={{ width: 150 }} value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
        <button className="btn btn-primary" style={{ padding: '4px 10px', fontSize: 12 }} onClick={add} disabled={saving}>
          {saving ? '…' : 'Add rate'}
        </button>
        {error && <span style={{ fontSize: 12, color: 'var(--red)' }}>⚠ {error}</span>}
      </div>

      <table className="data-table" style={{ width: '100%', fontSize: 13 }}>
        <thead><tr>
          <th style={{ textAlign: 'left' }}>Kind</th><th style={{ textAlign: 'left' }}>Rate</th>
          <th style={{ textAlign: 'left' }}>Effective</th><th style={{ textAlign: 'left' }}>Entered</th><th />
        </tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={5} style={{ color: 'var(--text-mid)' }}>No rates yet.</td></tr>}
          {rows.map(r => (
            <tr key={r.id}>
              <td>{r.kind}</td>
              <td>{money(r.rate)}</td>
              <td>
                {day(r.effectiveDate)}{' '}
                {currentIds.has(r.id) && <span className="badge badge-green">current</span>}
                {day(r.effectiveDate) > today && <span className="badge badge-yellow">scheduled</span>}
              </td>
              <td style={{ color: 'var(--text-mid)' }}>{day(r.createdAt)}</td>
              <td>
                {isAdmin && (
                  <button className="btn btn-outline" style={{ padding: '2px 8px', fontSize: 12 }} onClick={() => remove(r.id)}>
                    {confirmId === r.id ? 'Confirm delete' : 'Delete'}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BillingRates() {
  const { currentUser } = useApp();
  const isAdmin = !currentUser || currentUser.role === 'admin';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [missing, setMissing] = useState('all'); // all | services | license
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });
  const [selectedId, setSelectedId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(async () => {
    try { setRows(await apiFetch('/billing-rates')); setError(''); }
    catch (err) { setError(err.message); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const val = (r, k) => ({
    name: r.name.toLowerCase(), projectType: r.projectType, projectStatus: r.projectStatus,
    services: r.services.current?.rate ?? -1, servicesSince: day(r.services.current?.effectiveDate),
    license: r.license.current?.rate ?? -1, licenseSince: day(r.license.current?.effectiveDate),
  }[k]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = rows.filter(r =>
      (showArchived || r.projectStatus !== 'Archived') &&
      (!q || r.name.toLowerCase().includes(q)) &&
      (missing !== 'services' || !r.services.current) &&
      (missing !== 'license' || !r.license.current));
    const dir = sort.dir === 'desc' ? -1 : 1;
    return [...list].sort((a, b) => (val(a, sort.key) > val(b, sort.key) ? dir : val(a, sort.key) < val(b, sort.key) ? -dir : 0));
  }, [rows, search, missing, sort, showArchived]);

  const handleSort = (key) => setSort(p => (p.key === key ? { key, dir: p.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  const noServices = rows.filter(r => !r.services.current).length;
  const selected = rows.find(r => r.id === selectedId);

  return (
    <div>
      <div className="page-header">
        <div className="page-eyebrow">Financials</div>
        <h1 className="page-title">Billing Rates</h1>
        <p className="page-subtitle">
          Each project's Services ($/hr) and License (SW run rate) rates over time. A change is a new
          effective-dated row, so earlier months keep the rate they were calculated against.
        </p>
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
          <input className="form-input" style={{ width: 240 }} placeholder="Search projects…" value={search} onChange={(e) => setSearch(e.target.value)} />
          <select className="form-select" style={{ width: 200 }} value={missing} onChange={(e) => setMissing(e.target.value)}>
            <option value="all">All projects</option>
            <option value="services">Missing Services rate</option>
            <option value="license">Missing License rate</option>
          </select>
          <label style={{ fontSize: 13, display: 'flex', gap: 4, alignItems: 'center' }}>
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Show archived
          </label>
          <span style={{ fontSize: 12, color: 'var(--text-mid)' }}>
            {shown.length} of {rows.length} · {noServices} without a Services rate
          </span>
          <Link to="/project-budgets" style={{ marginLeft: 'auto', fontSize: 12 }}>Project Budgets →</Link>
        </div>

        {error && <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 8 }}>⚠ {error}</div>}
        {loading ? <div style={{ fontSize: 13 }}>Loading…</div> : (
          <table className="data-table" style={{ width: '100%', fontSize: 13 }}>
            <thead><tr>
              <SortTh label="Project" sortKey="name" sort={sort} onSort={handleSort} />
              <SortTh label="Type" sortKey="projectType" sort={sort} onSort={handleSort} />
              <SortTh label="Status" sortKey="projectStatus" sort={sort} onSort={handleSort} />
              <SortTh label="Services $/hr" sortKey="services" sort={sort} onSort={handleSort} />
              <SortTh label="Since" sortKey="servicesSince" sort={sort} onSort={handleSort} />
              <SortTh label="License $/hr" sortKey="license" sort={sort} onSort={handleSort} />
              <SortTh label="Since" sortKey="licenseSince" sort={sort} onSort={handleSort} />
              <th />
            </tr></thead>
            <tbody>
              {shown.map(r => (
                <tr key={r.id} style={r.id === selectedId ? { background: 'var(--bg-light, #f5f7fa)' } : undefined}>
                  <td>{r.name}</td>
                  <td>{r.projectType}</td>
                  <td>{r.projectStatus}</td>
                  <td>{r.services.current ? money(r.services.current.rate) : <span className="badge badge-yellow">missing</span>}</td>
                  <td style={{ color: 'var(--text-mid)' }}>{day(r.services.current?.effectiveDate)}{r.services.scheduled > 0 && ` (+${r.services.scheduled} scheduled)`}</td>
                  <td>{money(r.license.current?.rate)}</td>
                  <td style={{ color: 'var(--text-mid)' }}>{day(r.license.current?.effectiveDate)}{r.license.scheduled > 0 && ` (+${r.license.scheduled} scheduled)`}</td>
                  <td>
                    <button className="btn btn-outline" style={{ padding: '2px 8px', fontSize: 12 }} onClick={() => setSelectedId(r.id)}>
                      History / edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <HistoryPanel key={selected.id} project={selected} isAdmin={isAdmin} onClose={() => setSelectedId(null)} onChanged={load} />
      )}
    </div>
  );
}

export default BillingRates;
